import React, { useState } from 'react';
import {
  Bell, Mail, CheckCircle, XCircle, Clock, AlertCircle,
  ShoppingBag, Package, Users, Store, DollarSign, Star,
  MessageSquare, Calendar, Eye, Download, Filter,
  ChevronLeft, ChevronRight, Trash2, CheckCheck,
  BellOff, Volume2, VolumeX, Archive, RefreshCw,
  TrendingUp, Truck, Heart, Award, Crown, Sparkles,
  FileText, Search
} from 'lucide-react';
import { Link } from 'react-router-dom';

const PartnerNotifications = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const itemsPerPage = 6;

  // Static Mock Data
  const notificationsData = [
    {
      id: 'NOT-001',
      title: 'New Order Received',
      message: 'Order #ORD-7829 has been placed by Rahul Sharma worth ₹2,450',
      type: 'order',
      priority: 'high',
      date: 'Oct 24, 2024',
      time: '10:30 AM',
      read: false,
      icon: ShoppingBag,
      actionLink: '/partner/orders'
    },
    {
      id: 'NOT-002',
      title: 'New Seller Registered',
      message: 'Green Thumb Nursery has joined the platform as a new seller',
      type: 'seller',
      priority: 'high',
      date: 'Oct 23, 2024',
      time: '02:15 PM',
      read: false,
      icon: Store,
      actionLink: '/partner/sellers'
    },
    {
      id: 'NOT-003',
      title: 'Platform Revenue Update',
      message: 'Revenue increased by 17% this month. Total revenue: ₹6.12L',
      type: 'revenue',
      priority: 'medium',
      date: 'Oct 23, 2024',
      time: '11:45 AM',
      read: true,
      icon: DollarSign,
      actionLink: '/partner/revenue'
    },
    {
      id: 'NOT-004',
      title: 'Customer Review Alert',
      message: 'Amit Singh left a 5-star review on Monstera Deliciosa',
      type: 'review',
      priority: 'medium',
      date: 'Oct 22, 2024',
      time: '09:20 AM',
      read: false,
      icon: Star,
      actionLink: '/partner/products'
    },
    {
      id: 'NOT-005',
      title: 'Payout Processed',
      message: 'Partner payout of ₹5,20,200 has been processed for October',
      type: 'payout',
      priority: 'high',
      date: 'Oct 21, 2024',
      time: '04:30 PM',
      read: true,
      icon: DollarSign,
      actionLink: '/partner/revenue'
    },
    {
      id: 'NOT-006',
      title: 'New Customer Registered',
      message: 'Priya Patel has created a new customer account',
      type: 'customer',
      priority: 'low',
      date: 'Oct 20, 2024',
      time: '01:00 PM',
      read: true,
      icon: Users,
      actionLink: '/partner/users'
    },
    {
      id: 'NOT-007',
      title: 'System Update Complete',
      message: 'Platform update completed successfully. New features added.',
      type: 'system',
      priority: 'low',
      date: 'Oct 20, 2024',
      time: '02:15 AM',
      read: true,
      icon: Bell,
      actionLink: null
    },
    {
      id: 'NOT-008',
      title: 'High Traffic Alert',
      message: 'Platform traffic increased by 25% today. Peak hours 7-9 PM.',
      type: 'alert',
      priority: 'high',
      date: 'Oct 19, 2024',
      time: '11:30 AM',
      read: false,
      icon: TrendingUp,
      actionLink: null
    },
    {
      id: 'NOT-009',
      title: 'Delivery Performance',
      message: 'Average delivery time improved to 3.2 days this week',
      type: 'shipping',
      priority: 'medium',
      date: 'Oct 18, 2024',
      time: '03:45 PM',
      read: true,
      icon: Truck,
      actionLink: null
    },
    {
      id: 'NOT-010',
      title: 'Monthly Report Ready',
      message: 'October 2024 performance report is ready for download',
      type: 'report',
      priority: 'low',
      date: 'Oct 17, 2024',
      time: '10:00 AM',
      read: false,
      icon: FileText,
      actionLink: '/partner/reports'
    },
  ];

  const stats = {
    total: notificationsData.length,
    unread: notificationsData.filter(n => !n.read).length,
    read: notificationsData.filter(n => n.read).length,
    highPriority: notificationsData.filter(n => n.priority === 'high').length,
    today: notificationsData.filter(n => n.date === 'Oct 24, 2024').length
  };

  const getTypeConfig = (type) => {
    switch (type) {
      case 'order': return { label: 'Order', icon: ShoppingBag, color: '#3b82f6', bg: '#dbeafe' };
      case 'seller': return { label: 'Seller', icon: Store, color: '#8b5cf6', bg: '#e9d5ff' };
      case 'revenue': return { label: 'Revenue', icon: DollarSign, color: '#10b981', bg: '#d1fae5' };
      case 'review': return { label: 'Review', icon: Star, color: '#f59e0b', bg: '#fed7aa' };
      case 'payout': return { label: 'Payout', icon: DollarSign, color: '#ef4444', bg: '#fee2e2' };
      case 'customer': return { label: 'Customer', icon: Users, color: '#06b6d4', bg: '#cffafe' };
      case 'system': return { label: 'System', icon: Bell, color: '#64748b', bg: '#f1f5f9' };
      case 'alert': return { label: 'Alert', icon: AlertCircle, color: '#ef4444', bg: '#fee2e2' };
      case 'shipping': return { label: 'Shipping', icon: Truck, color: '#3b82f6', bg: '#dbeafe' };
      case 'report': return { label: 'Report', icon: FileText, color: '#8b5cf6', bg: '#e9d5ff' };
      default: return { label: type, icon: Bell, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  const getPriorityConfig = (priority) => {
    switch (priority) {
      case 'high': return { label: 'High', icon: AlertCircle, color: '#ef4444', bg: '#fee2e2' };
      case 'medium': return { label: 'Medium', icon: Clock, color: '#f59e0b', bg: '#fed7aa' };
      default: return { label: 'Low', icon: Bell, color: '#10b981', bg: '#d1fae5' };
    }
  };

  const filteredNotifications = notificationsData.filter(notification => {
    const matchesSearch = notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          notification.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || notification.type === filterType;
    const matchesPriority = filterPriority === 'all' || notification.priority === filterPriority;
    return matchesSearch && matchesType && matchesPriority;
  });

  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);
  const paginatedNotifications = filteredNotifications.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div className="stat-card">
      <div className="stat-icon" style={{ backgroundColor: bgColor }}>
        <Icon size={20} color={color} />
      </div>
      <div className="stat-info">
        <h3>{value}</h3>
        <span>{title}</span>
      </div>
    </div>
  );

  const markAsRead = (id) => {
    // In real implementation, update state and API
    alert(`Notification ${id} marked as read`);
  };

  const markAllAsRead = () => {
    alert('All notifications marked as read');
  };

  const deleteNotification = (id) => {
    if (window.confirm('Delete this notification?')) {
      alert(`Notification ${id} deleted`);
    }
  };

  const deleteSelected = () => {
    if (window.confirm(`Delete ${selectedNotifications.length} notifications?`)) {
      alert(`${selectedNotifications.length} notifications deleted`);
      setSelectedNotifications([]);
    }
  };

  const toggleSelectNotification = (id) => {
    if (selectedNotifications.includes(id)) {
      setSelectedNotifications(selectedNotifications.filter(n => n !== id));
    } else {
      setSelectedNotifications([...selectedNotifications, id]);
    }
  };

  const selectAll = () => {
    if (selectedNotifications.length === paginatedNotifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(paginatedNotifications.map(n => n.id));
    }
  };

  const formatTimeAgo = (date, time) => {
    return `${date} at ${time}`;
  };

  return (
    <div className="partner-notifications-modern">
      {/* Header */}
      <div className="notifications-header">
        <div>
          <h1 className="page-title">Notifications</h1>
          <p className="page-subtitle">Stay updated with platform activities and alerts</p>
        </div>
        <div className="header-actions">
          <button className="btn-export">
            <Download size={16} />
            Export
          </button>
          <button className="btn-primary" onClick={markAllAsRead}>
            <CheckCheck size={16} />
            Mark All as Read
          </button>
        </div>
      </div>

      {/* Read-Only Banner */}
      <div className="readonly-banner">
        <Eye size={18} />
        <span>You have read-only access. You can view notifications but cannot modify settings.</span>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <StatCard title="Total Notifications" value={stats.total} icon={Bell} color="#10b981" bgColor="#d1fae5" />
        <StatCard title="Unread" value={stats.unread} icon={Mail} color="#3b82f6" bgColor="#dbeafe" />
        <StatCard title="High Priority" value={stats.highPriority} icon={AlertCircle} color="#ef4444" bgColor="#fee2e2" />
        <StatCard title="Today" value={stats.today} icon={Calendar} color="#f59e0b" bgColor="#fed7aa" />
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search notifications..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select className="filter-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All Types</option>
            <option value="order">Orders</option>
            <option value="seller">Sellers</option>
            <option value="revenue">Revenue</option>
            <option value="review">Reviews</option>
            <option value="payout">Payouts</option>
            <option value="customer">Customers</option>
            <option value="system">System</option>
            <option value="alert">Alerts</option>
            <option value="shipping">Shipping</option>
            <option value="report">Reports</option>
          </select>
          <select className="filter-select" value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
            <option value="all">All Priority</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          {selectedNotifications.length > 0 && (
            <button className="btn-delete-selected" onClick={deleteSelected}>
              <Trash2 size={16} />
              Delete Selected ({selectedNotifications.length})
            </button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <div className="notifications-container">
        <div className="list-header">
          <label className="checkbox-label">
            <input 
              type="checkbox" 
              checked={selectedNotifications.length === paginatedNotifications.length && paginatedNotifications.length > 0}
              onChange={selectAll}
            />
            <span>Select All</span>
          </label>
          <div className="list-header-info">
            {filteredNotifications.length} notifications found
          </div>
        </div>

        <div className="notifications-list">
          {paginatedNotifications.map((notification) => {
            const typeConfig = getTypeConfig(notification.type);
            const priorityConfig = getPriorityConfig(notification.priority);
            const TypeIcon = typeConfig.icon;
            const PriorityIcon = priorityConfig.icon;
            const NotificationIcon = notification.icon;
            
            return (
              <div key={notification.id} className={`notification-item ${!notification.read ? 'unread' : ''}`}>
                <div className="notification-checkbox">
                  <input 
                    type="checkbox" 
                    checked={selectedNotifications.includes(notification.id)}
                    onChange={() => toggleSelectNotification(notification.id)}
                  />
                </div>
                <div className="notification-icon" style={{ backgroundColor: typeConfig.bg }}>
                  <TypeIcon size={20} color={typeConfig.color} />
                </div>
                <div className="notification-content">
                  <div className="notification-header">
                    <div className="notification-title-section">
                      <h4 className="notification-title">{notification.title}</h4>
                      {!notification.read && <span className="unread-badge">New</span>}
                    </div>
                    <div className="notification-time">
                      <Clock size={12} />
                      <span>{formatTimeAgo(notification.date, notification.time)}</span>
                    </div>
                  </div>
                  <p className="notification-message">{notification.message}</p>
                  <div className="notification-footer">
                    <div className="notification-badges">
                      <span className="type-badge" style={{ backgroundColor: typeConfig.bg, color: typeConfig.color }}>
                        <TypeIcon size={10} />
                        {typeConfig.label}
                      </span>
                      <span className="priority-badge" style={{ backgroundColor: priorityConfig.bg, color: priorityConfig.color }}>
                        <PriorityIcon size={10} />
                        {priorityConfig.label}
                      </span>
                    </div>
                    <div className="notification-actions">
                      {!notification.read && (
                        <button className="action-btn read" onClick={() => markAsRead(notification.id)}>
                          <Eye size={14} />
                          Mark as Read
                        </button>
                      )}
                      {notification.actionLink && (
                        <Link to={notification.actionLink} className="action-btn view">
                          <Eye size={14} />
                          View Details
                        </Link>
                      )}
                      <button className="action-btn delete" onClick={() => deleteNotification(notification.id)}>
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination-wrapper">
            <div className="pagination-info">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredNotifications.length)} of {filteredNotifications.length} notifications
            </div>
            <div className="pagination-buttons">
              <button 
                className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`} 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={16} /> Previous
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button 
                  key={i}
                  className={`page-number ${currentPage === i + 1 ? 'active' : ''}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`} 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredNotifications.length === 0 && (
          <div className="empty-state">
            <Bell size={64} className="empty-icon" />
            <h3>No notifications</h3>
            <p>You're all caught up! No new notifications to display.</p>
          </div>
        )}
      </div>

      {/* Quick Insights */}
      <div className="insights-card">
        <div className="insights-header">
          <Sparkles size={20} />
          <h3>Notification Insights</h3>
        </div>
        <div className="insights-grid">
          <div className="insight-item">
            <div className="insight-icon">📊</div>
            <div>
              <h4>Most Active Time</h4>
              <p>Notifications peak between 10 AM - 12 PM and 4 PM - 6 PM</p>
            </div>
          </div>
          <div className="insight-item">
            <div className="insight-icon">🎯</div>
            <div>
              <h4>High Priority Alerts</h4>
              <p>{stats.highPriority} high priority notifications require attention</p>
            </div>
          </div>
          <div className="insight-item">
            <div className="insight-icon">📈</div>
            <div>
              <h4>Notification Trend</h4>
              <p>Notification volume increased by 15% this month</p>
            </div>
          </div>
          <div className="insight-item">
            <div className="insight-icon">⚡</div>
            <div>
              <h4>Response Time</h4>
              <p>Average response time to alerts: 2.5 hours</p>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .partner-notifications-modern {
          padding: 24px;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          min-height: 100vh;
        }

        /* Header */
        .notifications-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 28px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .page-title {
          font-size: 28px;
          font-weight: 700;
          background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 4px;
        }

        .page-subtitle {
          color: #64748b;
          font-size: 14px;
          margin: 0;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .btn-export, .btn-primary {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          border-radius: 40px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
        }

        .btn-export {
          background: white;
          border: 1px solid #e2e8f0;
          color: #475569;
        }

        .btn-export:hover {
          border-color: #10b981;
          color: #10b981;
        }

        .btn-primary {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border: none;
          color: white;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        /* Read-Only Banner */
        .readonly-banner {
          background: #fef3c7;
          border: 1px solid #fde68a;
          border-radius: 12px;
          padding: 12px 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
          color: #92400e;
          font-size: 13px;
        }

        /* Stats Cards */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 28px;
        }

        .stat-card {
          background: white;
          border-radius: 20px;
          padding: 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          border: 1px solid #e8ecef;
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.1);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 14px;
        }

        .stat-info h3 {
          font-size: 20px;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }

        .stat-info span {
          font-size: 11px;
          color: #64748b;
        }

        /* Filters */
        .filters-bar {
          background: white;
          border-radius: 16px;
          padding: 12px 20px;
          margin-bottom: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
          border: 1px solid #e8ecef;
        }

        .search-box {
          position: relative;
          flex: 1;
          max-width: 320px;
        }

        .search-box svg {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
        }

        .search-box input {
          width: 100%;
          padding: 8px 16px 8px 38px;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
          font-size: 13px;
        }

        .filter-group {
          display: flex;
          gap: 12px;
          align-items: center;
          flex-wrap: wrap;
        }

        .filter-select {
          padding: 6px 16px;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
          font-size: 13px;
          background: white;
        }

        .btn-delete-selected {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 16px;
          background: #fee2e2;
          border: none;
          border-radius: 40px;
          font-size: 12px;
          font-weight: 500;
          color: #ef4444;
          cursor: pointer;
        }

        /* Notifications Container */
        .notifications-container {
          background: white;
          border-radius: 20px;
          padding: 20px;
          margin-bottom: 24px;
          border: 1px solid #e8ecef;
        }

        .list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 16px;
          margin-bottom: 16px;
          border-bottom: 1px solid #e8ecef;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 500;
          color: #64748b;
          cursor: pointer;
        }

        .checkbox-label input {
          width: 16px;
          height: 16px;
          cursor: pointer;
        }

        .list-header-info {
          font-size: 12px;
          color: #94a3b8;
        }

        /* Notifications List */
        .notifications-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .notification-item {
          display: flex;
          gap: 16px;
          padding: 20px;
          background: white;
          border-radius: 16px;
          transition: all 0.3s ease;
          border: 1px solid #e8ecef;
        }

        .notification-item.unread {
          background: #f0fdf4;
          border-left: 4px solid #10b981;
        }

        .notification-item:hover {
          background: #f8fafc;
          transform: translateX(4px);
        }

        .notification-checkbox {
          padding-top: 4px;
        }

        .notification-checkbox input {
          width: 16px;
          height: 16px;
          cursor: pointer;
        }

        .notification-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .notification-content {
          flex: 1;
        }

        .notification-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;
          flex-wrap: wrap;
          gap: 8px;
        }

        .notification-title-section {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .notification-title {
          font-size: 15px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .unread-badge {
          padding: 2px 8px;
          background: #10b981;
          color: white;
          border-radius: 20px;
          font-size: 10px;
          font-weight: 600;
        }

        .notification-time {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          color: #94a3b8;
        }

        .notification-message {
          font-size: 12px;
          color: #475569;
          line-height: 1.5;
          margin: 0 0 12px;
        }

        .notification-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }

        .notification-badges {
          display: flex;
          gap: 8px;
        }

        .type-badge, .priority-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 10px;
          border-radius: 20px;
          font-size: 10px;
          font-weight: 500;
        }

        .notification-actions {
          display: flex;
          gap: 8px;
        }

        .action-btn {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border: none;
          background: transparent;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
        }

        .action-btn.read:hover {
          background: #dbeafe;
          color: #3b82f6;
        }

        .action-btn.view:hover {
          background: #d1fae5;
          color: #10b981;
        }

        .action-btn.delete:hover {
          background: #fee2e2;
          color: #ef4444;
        }

        /* Pagination */
        .pagination-wrapper {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 20px;
          margin-top: 20px;
          border-top: 1px solid #e8ecef;
          flex-wrap: wrap;
          gap: 16px;
        }

        .pagination-info {
          font-size: 13px;
          color: #64748b;
        }

        .pagination-buttons {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .page-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          font-size: 13px;
          cursor: pointer;
        }

        .page-btn.disabled, .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .page-number {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          cursor: pointer;
          font-size: 13px;
        }

        .page-number.active {
          background: #10b981;
          border-color: #10b981;
          color: white;
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .empty-icon {
          color: #94a3b8;
          margin-bottom: 20px;
        }

        .empty-state h3 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 8px;
        }

        .empty-state p {
          font-size: 13px;
          color: #64748b;
          margin: 0;
        }

        /* Insights Card */
        .insights-card {
          background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
          border-radius: 20px;
          padding: 24px;
        }

        .insights-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 20px;
        }

        .insights-header svg {
          color: #f59e0b;
        }

        .insights-header h3 {
          font-size: 16px;
          font-weight: 600;
          color: white;
          margin: 0;
        }

        .insights-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .insight-item {
          display: flex;
          gap: 14px;
          padding: 16px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          backdrop-filter: blur(10px);
        }

        .insight-icon {
          font-size: 28px;
        }

        .insight-item h4 {
          font-size: 13px;
          font-weight: 600;
          color: white;
          margin: 0 0 4px;
        }

        .insight-item p {
          font-size: 11px;
          color: rgba(255, 255, 255, 0.8);
          margin: 0;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .partner-notifications-modern {
            padding: 16px;
          }
          .stats-grid {
            grid-template-columns: 1fr;
          }
          .filters-bar {
            flex-direction: column;
            align-items: stretch;
          }
          .search-box {
            max-width: none;
          }
          .filter-group {
            flex-direction: column;
          }
          .notification-item {
            flex-direction: column;
          }
          .notification-header {
            flex-direction: column;
          }
          .notification-footer {
            flex-direction: column;
            align-items: flex-start;
          }
          .insights-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default PartnerNotifications;